import { useCallback } from "react";
import { API } from "../../../lib/api";
import { ChartConfig } from "../Chart";

export const useChartImageGenerator = () => {
  const generateCanvasFromChart = useCallback(
    async (chartElement: HTMLElement, config: ChartConfig): Promise<string> => {
      try {
        const canvas = await htmlToCanvas(chartElement, config);
        const imageBlob = await canvasToBlob(canvas);
        const imageUrl = await uploadImageToServer(imageBlob);
        return imageUrl;
      } catch (error) {
        console.error("Erro ao gerar imagem do gráfico:", error);
        throw error;
      }
    },
    []
  );

  const htmlToCanvas = async (
    element: HTMLElement,
    config: ChartConfig
  ): Promise<HTMLCanvasElement> => {
    // Verificar se há legenda customizada (div com flex flex-col)
    const customLegend = element.querySelector(
      ".flex.flex-col.items-start.mt-4.space-y-2"
    );

    if (customLegend) {
      // Usar abordagem diferente para capturar elemento completo com legenda customizada
      return createCanvasWithCustomLegend(element, config);
    }

    // Usar canvas nativo baseado em SVG do Google Charts (abordagem original)
    return createCanvasFromGoogleChart(element, config);
  };

  const calculateTitleHeight = (config: ChartConfig): number => {
    // Altura do título no topo (mb-4 = 16px margin-bottom + ~20px altura do texto)
    return config.title ? 50 : 0;
  };

  /** Força preto nos textos do SVG (anotações). O CSS não vale na exportação para imagem. */
  const forceAnnotationTextBlack = (container: Element) => {
    container.querySelectorAll("text, tspan").forEach((el) => {
      el.setAttribute("fill", "#000000");
    });
  };

  const calculateLegendHeight = (element: HTMLElement): number => {
    const legendContainer = element.querySelector(
      ".flex.flex-col.items-start.mt-4.space-y-2"
    );
    if (!legendContainer) return 0;

    let height = 0;

    // Altura dos itens da legenda
    const legendItems = legendContainer.querySelectorAll(".flex.items-center");
    height += legendItems.length * 30; // 30px por item

    return height;
  };

  const createCanvasWithCustomLegend = async (
    element: HTMLElement,
    config: ChartConfig
  ): Promise<HTMLCanvasElement> => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error("Não foi possível criar contexto do canvas");
    }

    // Calcular altura necessária baseada no conteúdo real
    const titleHeight = calculateTitleHeight(config);
    const legendHeight = calculateLegendHeight(element);

    // Preencher fundo branco
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Aguardar renderização
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Capturar o SVG do gráfico e desenhar a legenda customizada manualmente
    const svg = element.querySelector("svg");
    if (svg) {
      try {
        const svgClone = svg.cloneNode(true) as SVGElement;
        forceAnnotationTextBlack(svgClone);
        const svgData = new XMLSerializer().serializeToString(svgClone);
        const img = await createImageFromSVG(svgData);

        canvas.width = img.width;
        canvas.height = titleHeight + img.height + legendHeight;

        // Preencher fundo branco novamente com o tamanho correto
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        let currentY = 0;

        // Desenhar título no topo se existir
        if (config.title) {
          await drawTitle(ctx, config.title, 0, currentY, img.width);
          currentY += titleHeight;
        }

        // Desenhar o gráfico abaixo do título
        ctx.drawImage(img, 0, currentY, img.width, img.height);
        currentY += img.height;

        // Desenhar a legenda customizada abaixo do gráfico
        await drawCustomLegend(ctx, element, 0, currentY);
      } catch (error) {
        console.warn("Erro ao processar SVG:", error);
        drawPlaceholder(ctx, canvas.width, canvas.height);
      }
    } else {
      console.warn("SVG não encontrado");
      drawPlaceholder(ctx, canvas.width, canvas.height);
    }

    return canvas;
  };

  const drawTitle = async (
    ctx: CanvasRenderingContext2D,
    title: string,
    startX: number,
    startY: number,
    width: number
  ) => {
    ctx.fillStyle = "#1f2937"; // text-gray-800
    ctx.font = "16px Arial"; // text-base font-normal
    ctx.textBaseline = "top";
    ctx.textAlign = "left";
    
    // Desenhar título com margem inferior (mb-4 = 16px)
    const titleY = startY + 16; // Espaçamento superior
    ctx.fillText(title, startX, titleY);
  };

  const drawCustomLegend = async (
    ctx: CanvasRenderingContext2D,
    element: HTMLElement,
    startX: number,
    startY: number
  ) => {
    const legendContainer = element.querySelector(
      ".flex.flex-col.items-start.mt-4.space-y-2"
    );
    if (!legendContainer) return;

    let currentY = startY + 16; // Espaçamento superior (mt-4)

    // Desenhar itens da legenda
    const legendItems = legendContainer.querySelectorAll(".flex.items-center");
    legendItems.forEach((item) => {
      const colorElement = item.querySelector(
        'div[style*="background-color"]'
      ) as HTMLElement;
      const textElement = item.querySelector("span");

      if (colorElement && textElement) {
        const backgroundColor = colorElement.style.backgroundColor;
        const text = textElement.textContent || "";

        // Desenhar círculo de cor
        ctx.fillStyle = backgroundColor;
        ctx.beginPath();
        ctx.arc(startX + 6, currentY + 6, 6, 0, 2 * Math.PI);
        ctx.fill();

        // Desenhar texto com fonte maior para compensar a renderização no canvas
        ctx.fillStyle = "#374151"; // text-gray-700
        ctx.font = "12px Arial";
        ctx.textBaseline = "middle"; // Melhor alinhamento vertical
        ctx.fillText(text, startX + 20, currentY + 8);

        currentY += 30; // Espaçamento entre itens (aumentado para fonte maior)
      }
    });
  };

  const createCanvasFromGoogleChart = async (
    element: HTMLElement,
    config: ChartConfig
  ): Promise<HTMLCanvasElement> => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error("Não foi possível criar contexto do canvas");
    }

    await new Promise((resolve) => setTimeout(resolve, 500));
    const svg = element.querySelector("svg");

    if (svg) {
      try {
        // Clonar o SVG para não modificar o original
        const svgClone = svg.cloneNode(true) as SVGElement;
        
        // Remover linhas horizontais que aparecem abaixo da legenda
        // Estratégia: remover todas as linhas horizontais finas na região superior do gráfico
        const removeHorizontalLines = (container: Element) => {
          // Remover elementos <line> horizontais
          const lines = container.querySelectorAll('line');
          lines.forEach((line) => {
            const x1 = parseFloat(line.getAttribute('x1') || '0');
            const x2 = parseFloat(line.getAttribute('x2') || '0');
            const y1 = parseFloat(line.getAttribute('y1') || '0');
            const y2 = parseFloat(line.getAttribute('y2') || '0');
            const strokeWidth = parseFloat(line.getAttribute('stroke-width') || '1');
            const stroke = (line.getAttribute('stroke') || '').toLowerCase();
            
            const isHorizontal = Math.abs(y1 - y2) < 1;
            // Ampliar a região para capturar a linha (20-120px do topo)
            const isInLegendArea = y1 >= 20 && y1 <= 120;
            // Linha que se estende por pelo menos 50px (mais flexível)
            const isLongLine = Math.abs(x2 - x1) > 50;
            // Linhas finas (até 2px)
            const isThin = strokeWidth <= 2;
            // Não remover linhas pretas/escuras (que são importantes do gráfico)
            const isNotImportant = !stroke.includes('#000') && 
                                   !stroke.includes('#333') && 
                                   !stroke.includes('#666') &&
                                   stroke !== 'none';
            
            if (isHorizontal && isInLegendArea && isLongLine && isThin && isNotImportant) {
              line.remove();
            }
          });
          
          // Remover elementos <path> que sejam linhas horizontais
          const paths = container.querySelectorAll('path');
          paths.forEach((path) => {
            const d = path.getAttribute('d') || '';
            const strokeWidth = parseFloat(path.getAttribute('stroke-width') || '1');
            const stroke = (path.getAttribute('stroke') || '').toLowerCase();
            
            // Padrões de paths horizontais (mais flexíveis)
            const patterns = [
              /M\s*([\d.]+)\s*([\d.]+)\s*L\s*([\d.]+)\s*\2/i,
              /M\s*([\d.]+)\s*([\d.]+)\s*H\s*([\d.]+)/i,
              /M\s*([\d.]+)\s*([\d.]+)\s*l\s*([\d.]+)\s*0/i,
              /M\s*([\d.]+)\s*([\d.]+)\s*h\s*([\d.]+)/i,
            ];
            
            for (const pattern of patterns) {
              const match = d.match(pattern);
              if (match) {
                const y = parseFloat(match[2]);
                const x1 = parseFloat(match[1]);
                const x2 = parseFloat(match[3] || match[1]);
                const isInLegendArea = y >= 20 && y <= 120;
                const isLongLine = Math.abs(x2 - x1) > 50;
                const isThin = strokeWidth <= 2;
                const isNotImportant = !stroke.includes('#000') && 
                                       !stroke.includes('#333') && 
                                       !stroke.includes('#666') &&
                                       stroke !== 'none';
                
                if (isInLegendArea && isLongLine && isThin && isNotImportant) {
                  path.remove();
                  break;
                }
              }
            }
          });
        };
        
        // Remover retângulos horizontais finos que possam ser a linha
        const removeHorizontalRects = (container: Element) => {
          const rects = container.querySelectorAll('rect');
          rects.forEach((rect) => {
            const y = parseFloat(rect.getAttribute('y') || '0');
            const height = parseFloat(rect.getAttribute('height') || '0');
            const width = parseFloat(rect.getAttribute('width') || '0');
            const fill = (rect.getAttribute('fill') || '').toLowerCase();
            const stroke = (rect.getAttribute('stroke') || '').toLowerCase();
            
            // Retângulos horizontais finos na região da legenda
            const isThinRect = height <= 2 && width > 100;
            const isInLegendArea = y >= 20 && y <= 120;
            const isNotImportant = (!fill || fill === 'none' || fill.includes('#f') || fill.includes('#e')) &&
                                   (!stroke || stroke === 'none' || stroke.includes('#f') || stroke.includes('#e'));
            
            if (isThinRect && isInLegendArea && isNotImportant) {
              rect.remove();
            }
          });
        };
        
        // Aplicar remoção no SVG e em todos os grupos
        removeHorizontalLines(svgClone);
        removeHorizontalRects(svgClone);
        svgClone.querySelectorAll('g').forEach((group) => {
          removeHorizontalLines(group);
          removeHorizontalRects(group);
        });

        forceAnnotationTextBlack(svgClone);

        const svgData = new XMLSerializer().serializeToString(svgClone);
        const img = await createImageFromSVG(svgData);

        const titleHeight = calculateTitleHeight(config);
        canvas.width = img.width;
        canvas.height = titleHeight + img.height;

        // Preencher fundo branco
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        let currentY = 0;

        // Desenhar título no topo se existir
        if (config.title) {
          await drawTitle(ctx, config.title, 0, currentY, img.width);
          currentY += titleHeight;
        }

        // Desenhar o gráfico abaixo do título
        ctx.drawImage(img, 0, currentY, canvas.width, img.height);
      } catch (error) {
        console.warn("Erro ao processar SVG do Google Charts:", error);
        // Desenhar um placeholder como fallback
        drawPlaceholder(ctx, canvas.width, canvas.height);
      }
    } else {
      console.warn("SVG do Google Charts não encontrado");
      drawPlaceholder(ctx, canvas.width, canvas.height);
    }

    return canvas;
  };

  const drawPlaceholder = (
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number
  ) => {
    // Desenhar um placeholder
    ctx.fillStyle = "#f8f9fa";
    ctx.fillRect(50, 50, width - 100, height - 100);

    ctx.strokeStyle = "#dee2e6";
    ctx.lineWidth = 2;
    ctx.strokeRect(50, 50, width - 100, height - 100);

    ctx.fillStyle = "#6c757d";
    ctx.font = "24px Arial";
    ctx.textAlign = "center";
    ctx.fillText("Gráfico", width / 2, height / 2 - 10);

    ctx.font = "14px Arial";
    ctx.fillText(
      "Clique em 'Salvar' para gerar imagem",
      width / 2,
      height / 2 + 20
    );
  };

  const createImageFromSVG = (svgData: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = (error) => {
        console.error("Erro ao criar imagem do SVG:", error);
        reject(new Error("Erro ao criar imagem do SVG"));
      };

      // Usar encodeURIComponent para evitar problemas de encoding
      try {
        const encodedSvg = encodeURIComponent(svgData);
        img.src = `data:image/svg+xml;charset=utf-8,${encodedSvg}`;
      } catch (error) {
        // Fallback para base64
        img.src =
          "data:image/svg+xml;base64," +
          btoa(unescape(encodeURIComponent(svgData)));
      }
    });
  };

  const canvasToBlob = (canvas: HTMLCanvasElement): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Erro ao converter canvas para blob"));
        }
      }, "image/png");
    });
  };

  const uploadImageToServer = async (blob: Blob): Promise<string> => {
    const file = new File([blob], "chart.png", { type: "image/png" });
    return await API.uploadImage(file);
  };

  return { generateCanvasFromChart };
};
